import React, {memo, useCallback, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';
import {PasswordContainer} from '../../../../components/PasswordContainer';
import {useLocalization} from '../../../../hooks/localization';
import {ControlIconTypeModel, ControlTypeModel} from '../../../../types/control';
import {Configs} from '../../../../configs';
import './index.scss';

interface Props {
  controlTypeId: string;
  renderType: 'three_phase' | 'two_phase' | 'simple';
  onClose: () => void;
  onSave: (data: {is_custom_icon: boolean; custom_icon?: any}) => void;
  existing?: any;
}

export const IconPickerModal = memo(function IconPickerModal({controlTypeId, renderType, onClose, onSave, existing}: Props){
  const {t} = useLocalization();
  const iconTypeList: ControlIconTypeModel[] = useSelector((state:any)=>state.control.controlIconTypeList);
  const controlTypeList: ControlTypeModel[] = useSelector((state:any)=>state.control.controlTypeList);

  const filteredIconList = useMemo(()=>iconTypeList.filter(i=>i.control_type_id===controlTypeId && i.icon_type==='control'),[iconTypeList,controlTypeId]);
  const actionIconList = useMemo(() => {
    return iconTypeList
      .filter(i => i.icon_type === 'action')
      .sort((a, b) => a.id.localeCompare(b.id, undefined, { numeric: true }));
  }, [iconTypeList]);
  const controlType = controlTypeList.find(ct=>ct.id===controlTypeId);

  const defaultIcon: ControlIconTypeModel = useMemo(()=>({ id:'', name:t('default'), control_type_id:controlTypeId, image_set: controlType?.image_set as any, icon_type:'control'} as any),[controlTypeId,controlType,t]);

  const [selectedIcon,setSelectedIcon]=useState<ControlIconTypeModel>(existing?.custom_icon_id? filteredIconList.find(i=>i.id===existing.custom_icon_id): defaultIcon);
  const [openIcon,setOpenIcon]=useState<ControlIconTypeModel>(existing?.action_open_icon_id? actionIconList.find(i=>i.id===existing.action_open_icon_id): null);
  const [closeIcon,setCloseIcon]=useState<ControlIconTypeModel>(existing?.action_close_icon_id? actionIconList.find(i=>i.id===existing.action_close_icon_id): null);
  const [stopIcon,setStopIcon]=useState<ControlIconTypeModel>(existing?.action_stop_icon_id? actionIconList.find(i=>i.id===existing.action_stop_icon_id): null);

  const buildSrc = (p: string) => {
    if (!p) return '';
    if (p.startsWith('http')) return p;
    if (Configs.IMAGE_PREFIX) {
      return Configs.IMAGE_PREFIX + (p.startsWith('/') ? p.slice(1) : p);
    }

    return p.startsWith('/') ? p : `/${p}`;
  };

  const save=useCallback(()=>{
    const hasAction = (renderType==='three_phase'||renderType==='two_phase')&&(openIcon||closeIcon||stopIcon);
    const useCustom = selectedIcon.id!=='' || hasAction;
    if(!useCustom){
      onSave({is_custom_icon:false});
    }else{
      onSave({
        is_custom_icon:true,
        custom_icon:{
          ...(selectedIcon.id !== '' ? {custom_icon_id:selectedIcon.id}:{}),
          ...(openIcon?{action_open_icon_id:openIcon.id}:{}),
          ...(closeIcon?{action_close_icon_id:closeIcon.id}:{}),
          ...(renderType==='three_phase' && stopIcon?{action_stop_icon_id:stopIcon.id}:{}),
        }
      })
    }
  },[selectedIcon,openIcon,closeIcon,stopIcon,renderType,onSave]);

  return (
    <div className="icon-picker-backdrop">
      <PasswordContainer requiredRole="user" isFullscreen={true}>
        <div className="icon-picker-modal">
        <div className="icon-picker-buttons">
          <button className='reject' onClick={onClose}>{t('cancel')}</button>
          <button className='accept' onClick={save}>{t('save')}</button>
        </div>
          <span className="icon-picker-title">{t('select_icon')}</span>
          <div className="icon-picker-body">
            <div className={`icon-column ${(renderType==='simple') ? 'full-width' : ''}`}> 
            <div className="icon-picker-grid">
              {[defaultIcon, ...filteredIconList].map(ic => (
                <div
                  key={ic.id || 'def'}
                  onClick={() => setSelectedIcon(ic)}
                  className={`icon-picker-item ${
                    (selectedIcon?.id === ic.id || (!filteredIconList.some(f => f.id === selectedIcon?.id) && ic.id === defaultIcon.id)) 
                      ? 'selected' 
                      : ''
                  }`}
                >
                  <img src={buildSrc(ic.image_set.default)} alt={ic.name} />
                </div>
              ))}
            </div>
            </div>
            {(renderType==='three_phase'||renderType==='two_phase') && (
              <div className="action-column">
                <div className="icon-picker-action-section">
                  
                  { (renderType==='three_phase' ? ['open','stop','close'] : ['open','close']).map(act=>(
                    <div key={act} className="action-set">
                      <div className="action-name">{t(act)}</div>
                      <div className="action-icons-grid">
                        {actionIconList.map(ai=> (
                          <div key={ai.id+act} onClick={()=>{
                            if(act==='open')setOpenIcon(ai);
                            else if(act==='close')setCloseIcon(ai);
                            else setStopIcon(ai);
                          }} className={`action-icon ${(act==='open'?openIcon?.id===ai.id:act==='close'?closeIcon?.id===ai.id:stopIcon?.id===ai.id)?'selected':''}`}>
                            <img src={buildSrc(ai.image_set.default)} alt={ai.name} />
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </PasswordContainer>
    </div>
  );
}); 